/**
 * mSTEM Open Project Modal (PRJ-02 / LLD UC 07). Presentational component
 * — Redux wiring lives in src/containers/open-project-modal.jsx.
 *
 * LLD UC 07 layout:
 *   1. Search input (debounced 200ms, filters by name + description)
 *   2. MstemTable with 5 columns: Tên dự án | Mô tả | Loại thiết bị
 *      | Sửa lần cuối | Thao tác (per-row ℹ detail + "Tải dự án" buttons)
 *   3. Empty states: search-empty / list-empty / load-error
 *   4. Bottom "Đóng" button
 *
 * PRJ-02 S-2 ("Xem thông tin các dự án đã tạo"): the ℹ row action opens
 * ProjectDetailModal for that one project. Following the KitManagerModal
 * (SMH-04) pattern, the detail view REPLACES this list while open, so there is
 * never a modal stacked on a modal — closing detail returns to the list.
 *
 * Source data: mstem.myProjects[] (Phase 3 Wave 0 reducer + Wave 1a seed).
 *
 * The container handles search debounce, sorting (updatedAt DESC), and
 * dispatching the load handler (fake-load for mock entries via DP-05 /
 * real vmState load for user-saved projects).
 */
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';
import ProjectActionsCell from './project-actions-cell.jsx';
import ProjectDetailModal from './project-detail-modal.jsx';

import styles from './open-project-modal.css';

const messages = defineMessages({
    modalTitle: {
        defaultMessage: 'Mở dự án có sẵn',
        description: 'Title of Open Project modal (PRJ-02, LLD UC 07)',
        id: 'gen.project.open.title'
    },
    searchPlaceholder: {
        defaultMessage: 'Tìm kiếm dự án...',
        description: 'Search input placeholder (PRJ-02)',
        id: 'gen.project.search.placeholder'
    },
    searchLabel: {
        defaultMessage: 'Tìm kiếm dự án',
        description: 'Search input aria-label (PRJ-02)',
        id: 'gen.project.search.label'
    },
    colName: {
        defaultMessage: 'Tên dự án',
        description: 'OpenProjectModal table column: project name',
        id: 'gen.project.column.name'
    },
    colDescription: {
        defaultMessage: 'Mô tả',
        description: 'OpenProjectModal table column: description',
        id: 'gen.project.column.description'
    },
    colType: {
        defaultMessage: 'Loại thiết bị',
        description: 'OpenProjectModal table column: device type',
        id: 'gen.project.column.type'
    },
    colUpdated: {
        defaultMessage: 'Sửa lần cuối',
        description: 'OpenProjectModal table column: last updated date',
        id: 'gen.project.column.updatedAt'
    },
    colActions: {
        defaultMessage: 'Thao tác',
        description: 'OpenProjectModal table column: action buttons',
        id: 'gen.project.column.actions'
    },
    detailAction: {
        defaultMessage: 'Xem thông tin chi tiết dự án',
        description: 'Detail row action — opens ProjectDetailModal (PRJ-02 S-2)',
        id: 'gen.project.open.detail'
    },
    loadButton: {
        defaultMessage: 'Tải dự án',
        description: 'Load-project row action button label (PRJ-02)',
        id: 'gen.project.open.load'
    },
    loadingButton: {
        defaultMessage: 'Đang tải...',
        description: 'Loading state on Load button while vm.loadProject is in flight',
        id: 'gen.project.open.loading'
    },
    closeButton: {
        defaultMessage: 'Đóng',
        description: 'Close modal button label (PRJ-02)',
        id: 'gen.project.open.close'
    },
    emptySearch: {
        defaultMessage: 'Không tìm thấy dự án phù hợp',
        description: 'Empty state when search yields no results (PRJ-02)',
        id: 'gen.project.open.emptySearch'
    },
    emptyList: {
        defaultMessage: 'Bạn chưa có dự án nào. Tạo dự án mới hoặc mở từ máy tính.',
        description: 'Empty state when user has no projects at all (PRJ-02)',
        id: 'gen.project.open.emptyList'
    },
    loadError: {
        defaultMessage: 'Không thể tải thông tin dự án. Vui lòng thử lại.',
        description: 'Error empty state per LLD UC 07 (PRJ-02)',
        id: 'gen.project.open.loadError'
    }
});

// Device-type display labels — IDs already registered by
// src/components/new-project-modal/new-project-modal.jsx (Plan 03-02). We
// reference them via the runtime intl API rather than re-declaring them
// here to satisfy the phase-2-messages "No duplicate gen.* ids" test.
// react-intl 2.9 resolves the locale string from any matching id; the
// defaultMessage fallback in formatMessage() guards against missing
// translations at runtime (Phase 2 micro-2.1 visibility patterns).
const TYPE_LABEL_IDS = {
    robot: {id: 'gen.project.type.robot', defaultMessage: 'Robot mSTEM'},
    kit: {id: 'gen.project.type.kit', defaultMessage: 'Kit SmartHome'},
    none: {id: 'gen.project.type.none', defaultMessage: 'Không kết nối thiết bị'}
};

const formatDate = function (iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '—';
        // VN format: dd/mm/yyyy
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
        return '—';
    }
};

const typeLabel = function (type, intl) {
    const descriptor = TYPE_LABEL_IDS[type] || TYPE_LABEL_IDS.none;
    return intl.formatMessage(descriptor);
};

const OpenProjectModalComponent = ({
    intl,
    searchTerm,
    filteredProjects,
    hasProjects,
    loadingError,
    loadingId,
    detailProject,
    onSearch,
    onLoad,
    onDetail,
    onCloseDetail,
    onLoadDetail,
    onClose
}) => {
    const emptyMessage = loadingError ?
        intl.formatMessage(messages.loadError) :
        (hasProjects ?
            intl.formatMessage(messages.emptySearch) :
            intl.formatMessage(messages.emptyList));

    const columns = [
        {
            key: 'name',
            label: <FormattedMessage {...messages.colName} />,
            width: '220px'
        },
        {
            key: 'description',
            label: <FormattedMessage {...messages.colDescription} />
        },
        {
            key: 'type',
            label: <FormattedMessage {...messages.colType} />,
            width: '140px',
            render: row => typeLabel(row.type, intl)
        },
        {
            key: 'updatedAt',
            label: <FormattedMessage {...messages.colUpdated} />,
            width: '140px',
            render: row => formatDate(row.updatedAt)
        },
        {
            key: 'actions',
            label: <FormattedMessage {...messages.colActions} />,
            width: '180px',
            render: function renderActionCell (row) {
                return (
                    <ProjectActionsCell
                        detailLabel={intl.formatMessage(messages.detailAction)}
                        isLoading={loadingId === row.id}
                        loadLabel={intl.formatMessage(messages.loadButton)}
                        loadingLabel={intl.formatMessage(messages.loadingButton)}
                        row={row}
                        onDetail={onDetail}
                        onLoad={onLoad}
                    />
                );
            }
        }
    ];

    // PRJ-02 S-2: the detail view REPLACES the list (KitManagerModal pattern),
    // so the student never sees a modal stacked on a modal.
    if (detailProject) {
        return (
            <ProjectDetailModal
                isLoading={loadingId === detailProject.id}
                project={detailProject}
                onLoad={onLoadDetail}
                onRequestClose={onCloseDetail}
            />
        );
    }

    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(messages.modalTitle)}
            onRequestClose={onClose}
        >
            <div className={styles.body}>
                <div className={styles.toolbar}>
                    <input
                        aria-label={intl.formatMessage(messages.searchLabel)}
                        className={styles.searchInput}
                        placeholder={intl.formatMessage(messages.searchPlaceholder)}
                        type="search"
                        value={searchTerm}
                        onChange={onSearch}
                    />
                </div>
                <MstemTable
                    columns={columns}
                    data={filteredProjects}
                    emptyMessage={emptyMessage}
                />
                <div className={styles.footer}>
                    <button
                        className={styles.btnSecondary}
                        type="button"
                        onClick={onClose}
                    >
                        <FormattedMessage {...messages.closeButton} />
                    </button>
                </div>
            </div>
        </Modal>
    );
};

OpenProjectModalComponent.propTypes = {
    detailProject: PropTypes.shape({id: PropTypes.string, name: PropTypes.string}),
    filteredProjects: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    hasProjects: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    loadingError: PropTypes.bool,
    loadingId: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onCloseDetail: PropTypes.func.isRequired,
    onDetail: PropTypes.func.isRequired,
    onLoad: PropTypes.func.isRequired,
    onLoadDetail: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    searchTerm: PropTypes.string.isRequired
};

OpenProjectModalComponent.defaultProps = {
    detailProject: null,
    loadingError: false,
    loadingId: null
};

export default injectIntl(OpenProjectModalComponent);
